import React from 'react'
import cx from 'classnames'
import loadable from '@loadable/component'
import withHydrationOnDemand from 'react-hydration-on-demand'

/* Utils */
import {
  generateBuyBoxAreaProps,
  generateProductDetailCarouselProps,
  generateBuyBoxSellerProps,
  generateVariantPanelProps,
  generateTechSpecsProps,
  generateBestReviewProps,
  generateDeliveryOptionsProps,
  generateProductDescriptionProps,
  generateQuestionAndAnswerProps,
  generateRecommendationProps,
  generatePaymentOptionsProps,
  generateCampaignsProps,
  generateOtherMerchantsProps,
  generateSponsoredProductsProps,
  generateCampaignBundleProps,
  generateProductBadgesProps,
  generatePopulerKeywordProps,
  generateMerchantCouponsProps,
  generateVasProps,
  generatePremiumBannerProps,
  generateBuybackRowProps,
  generateMinQuantityProps,
  generateComparedProductsProps,
  generateAdsBannerProps,
} from './productMobileUtils'

/* Styles */
import s from '../../routes/mobile/product-detail/product-detail.scss'

/* Skeletons */
import MerchantCouponsSkeleton from '../../views/product/mobile/merchant-coupons/MerchantCouponsSkeleton'
import CampaignBundleSkeleton from '../../views/product/mobile/campaign-bundle/CampaignBundleSkeleton'
import VasSkeleton from '../../views/product/mobile/vas/VasSkeleton'
import PremiumContentSkeleton from '../../views/product/mobile/premium-content/PremiumContentSkeleton'
import ProductBadgesSkeleton from '../../views/product/mobile/product-badges/ProductBadgesSkeleton'
import BuybackRowSkeleton from '../../views/product/mobile/buyback-row/BuybackRowSkeleton'
import BuyBoxSellerSkeleton from '../../views/product/mobile/buybox-seller/BuyBoxSellerSkeleton'
import PaymentOptionsSkeleton from '../../views/product/mobile/payment-options/PaymentOptionsSkeleton'
import DeliveryOptionsSkeleton from '../../views/product/mobile/delivery-options/DeliveryOptionsSkeleton'
import CampaignsSkeleton from '../../views/product/mobile/campaigns/CampaignsSkeleton'
import BestQnaSkeleton from '../../views/product/mobile/best-qna/BestQnaSkeleton'
import MinQuantitySkeleton from '../../views/product/mobile/min-quantity/MinQuantitySkeleton'

/* Components SSR */
const ProductDetailCarousel = loadable(() => import('../../views/product/mobile/carousel/ProductDetailCarousel'))
const VariantPanel = loadable(() => import('../../views/product/mobile/variant/panel/VariantPanel'))
const BuyBoxArea = loadable(() => import('../../views/product/mobile/buybox-area/BuyBoxArea'))
const ProductDescription = loadable(() => import('../../views/product/mobile/product-description/ProductDescription'))
const BestReview = loadable(() => import('../../views/product/mobile/best-review/BestReview'))
const TechSpecs = loadable(() => import('../../views/product/mobile/tech-specs/TechSpecs'))
const PopularKeyword = loadable(() => import('../../views/product/mobile/popular-keyword/PopularKeyword'))
const Recommendation = loadable(() => import('../../views/product/mobile/recommendation/Recommendation'))
const SponsoredProducts = loadable(() => import('../../views/product/mobile/sponsored-products/SponsoredProducts'))
const PaymentOptionsRow = loadable(() => import('../../views/product/mobile/payment-options-row/PaymentOptions'))
const CampaignsRow = loadable(() => import('../../views/product/mobile/campaigns-row/Campaigns'))
const DeliveryOptionsRow = loadable(() => import('../../views/product/mobile/delivery-options-row/DeliveryOptions'))
const OtherMerchants = loadable(() => import('../../views/product/mobile/other-merchants/OtherMerchants'))
const ComparedProducts = loadable(() => import('../../components/ComparedProducts/ComparedProducts'))

/* Components CSR */
const AdxBanner = loadable(() => import('../../views/product/mobile/adx-banner/AdxBanner'), {
  ssr: false,
  fallback: <div className={s.adxBanner}/>
})
const BuyBoxSeller = loadable(() => import('../../views/product/mobile/buybox-seller/BuyboxSeller'), {
  ssr: false,
  fallback: <BuyBoxSellerSkeleton className={s.buyBoxSeller}/>
})
const PaymentOptions = loadable(() => import('../../views/product/mobile/payment-options/PaymentOptions'), {
  ssr: false,
  fallback: <PaymentOptionsSkeleton className={s.paymentOptions}/>
})
const Campaigns = loadable(() => import('../../views/product/mobile/campaigns/Campaigns'), {
  ssr: false,
  fallback: <CampaignsSkeleton className={cx(s.campaigns, s.Full)}/>
})
const DeliveryOptions = loadable(() => import('../../views/product/mobile/delivery-options/DeliveryOptions'), {
  ssr: false,
  fallback: <DeliveryOptionsSkeleton />
})
const ProductBadges = loadable(() => import('../../views/product/mobile/product-badges/ProductBadges'), {
  ssr: false,
  fallback: <ProductBadgesSkeleton className={s.productBadges}/>
})
const CampaignBundle = loadable(() => import('../../views/product/mobile/campaign-bundle/CampaignBundle'), {
  ssr: false,
  fallback: <CampaignBundleSkeleton className={s.campaignBundle}/>
})
const MerchantCoupons = loadable(() => import('../../views/product/mobile/merchant-coupons/MerchantCoupons'), {
  ssr: false,
  fallback: <MerchantCouponsSkeleton className={s.merchantCoupons}/>
})
const Vas = loadable(() => import('../../views/product/mobile/vas/Vas'), {
  ssr: false,
  fallback: <VasSkeleton className={s.vas}/>
})
const PremiumContent = loadable(() => import('../../views/product/mobile/premium-content/PremiumContent'), {
  ssr: false,
  fallback: <PremiumContentSkeleton className={s.premium}/>
})
const BuybackRow = loadable(() => import('../../views/product/mobile/buyback-row/BuybackRow'), {
  ssr: false,
  fallback: <BuybackRowSkeleton className={s.buybackRow}/>
})
const BestQna = loadable(() => import('../../views/product/mobile/best-qna/BestQna'), {
  ssr: false,
  fallback: <BestQnaSkeleton className={s.bestQna}/>
})
const MinQuantity = loadable(() => import('../../views/product/mobile/min-quantity/MinQuantity'), {
  ssr: false,
  fallback: <MinQuantitySkeleton className={s.minQuantity}/>
})

const INTERSECTION_ROOT_MARGIN = '50px 0px 100px 0px'

const PRODUCT_STATUS_TYPES = {
  PRE_ORDER: 'Preorder',
  REMOVED: 'RemovedItem',
  OUT_OF_STOCK: 'OutOfStock',
  IN_STOCK: 'InStock',
}

const PRODUCT_INFORMATION_TEXT = {
  IS_CAMPAIGN_PRODUCT: {
    description: 'Kampanya ürünüdür, satışı bulunmamaktadır',
  },
  IS_NO_STOCK: {
    description: 'Bu ürün geçici olarak temin edilememektedir.',
    subDescription: 'Ürün temin edildiğinde tekrar satışa sunulacaktır'
  },
  IS_CLOSED_PRODUCT: {
    description: 'Bu ürün şu an satılamamaktadır.'
  },
  IS_NO_COMBINATION: ' kombinasyonunda ürün bulunamamaktadır.'
}

const PRODUCT_PAYMENT_TEXT = {
  LoanOffer: 'alışveriş kredisi',
  BuynowPayLater: 'bnpl',
  Installments: 'taksit seçenekleri'
}

const PRODUCT_DETAIL_VIEW_TYPES = {
  IMAGE_BASED: '3:4',
  SUMMARY_BASED: '1:1',
  CONTENT_BASED: '4:3',
}

const PRODUCT_DETAIL_CAROUSEL_IMAGE_SIZES = {
  [PRODUCT_DETAIL_VIEW_TYPES.IMAGE_BASED]: {
    width: 375,
    height: 500
  },
  [PRODUCT_DETAIL_VIEW_TYPES.SUMMARY_BASED]: {
    width: 375,
    height: 375
  },
  [PRODUCT_DETAIL_VIEW_TYPES.CONTENT_BASED]: {
    width: 375,
    height: 281,
  }
}

const PRODUCT_DETAIL_EXTENDED_CAROUSEL_THUMBNAIL_SIZES = {
  [PRODUCT_DETAIL_VIEW_TYPES.IMAGE_BASED]: {
    width: 66,
    height: 88
  },
  [PRODUCT_DETAIL_VIEW_TYPES.SUMMARY_BASED]: {
    width: 66,
    height: 66
  },
  [PRODUCT_DETAIL_VIEW_TYPES.CONTENT_BASED]: {
    width: 88,
    height: 66,
  }
}

const PRODUCT_DETAIL_COMPONENTS = {
  Image: {
    component: ProductDetailCarousel,
    propsMapper: generateProductDetailCarouselProps,
  },
  Summary: {
    component: BuyBoxArea,
    propsMapper: generateBuyBoxAreaProps,
  },
  Variants: {
    component: VariantPanel,
    propsMapper: generateVariantPanelProps,
  },
  Merchant: {
    component: withHydrationOnDemand({on: ['visible']})(BuyBoxSeller),
    propsMapper: generateBuyBoxSellerProps,
  },
  ProductKeyFeatures: {
    component: withHydrationOnDemand({on: ['visible']})(TechSpecs),
    propsMapper: generateTechSpecsProps,
  },
  DeliveryOptionsRow: {
    component: withHydrationOnDemand({on: ['visible']})(DeliveryOptionsRow),
    propsMapper: generateDeliveryOptionsProps,
  },
  DeliveryOptionsFull: {
    component: withHydrationOnDemand({on: ['visible']})(DeliveryOptions),
    propsMapper: generateDeliveryOptionsProps,
  },
  PaymentOptionsFull: {
    component: withHydrationOnDemand({on: ['visible']})(PaymentOptions),
    propsMapper: generatePaymentOptionsProps
  },
  PaymentOptionsRow: {
    component: withHydrationOnDemand({on: ['visible']})(PaymentOptionsRow),
    propsMapper: generatePaymentOptionsProps
  },
  CampaignsFull: {
    component: withHydrationOnDemand({on: ['visible']})(Campaigns),
    propsMapper: generateCampaignsProps,
  },
  CampaignsRow: {
    component: withHydrationOnDemand({on: ['visible']})(CampaignsRow),
    propsMapper: generateCampaignsProps,
  },
  Review: {
    component: withHydrationOnDemand({on: [['scroll', () => document]]})(BestReview),
    propsMapper: generateBestReviewProps,
  },
  ProductDescription: {
    component: withHydrationOnDemand({on: ['visible']})(ProductDescription),
    propsMapper: generateProductDescriptionProps,
  },
  BestQa: {
    component: withHydrationOnDemand({on: ['visible']})(BestQna),
    propsMapper: generateQuestionAndAnswerProps,
  },
  Badges: {
    component: withHydrationOnDemand({on: ['visible']})(ProductBadges),
    propsMapper: generateProductBadgesProps,
  },
  Recommendation: {
    component: withHydrationOnDemand({on: ['visible']})(Recommendation),
    propsMapper: generateRecommendationProps,
  },
  SponsoredProducts: {
    component: withHydrationOnDemand({on: ['visible']})(SponsoredProducts),
    propsMapper: generateSponsoredProductsProps,
  },
  OtherMerchants: {
    component: withHydrationOnDemand({on: ['visible']})(OtherMerchants),
    propsMapper: generateOtherMerchantsProps,
  },
  BuyTogether: {
    component: withHydrationOnDemand({on: ['visible']})(CampaignBundle),
    propsMapper: generateCampaignBundleProps,
  },
  PopularKeyword: {
    component: withHydrationOnDemand({on: ['visible']})(PopularKeyword),
    propsMapper: generatePopulerKeywordProps,
  },
  MerchantCoupon: {
    component: withHydrationOnDemand({on: ['visible']})(MerchantCoupons),
    propsMapper: generateMerchantCouponsProps,
  },
  Premium: {
    component: withHydrationOnDemand({on: ['visible']})(PremiumContent),
    propsMapper: generatePremiumBannerProps,
  },
  Vas: {
    component: withHydrationOnDemand({on: ['visible']})(Vas),
    propsMapper: generateVasProps,
  },
  Buyback: {
    component: withHydrationOnDemand({on: ['visible']})(BuybackRow),
    propsMapper: generateBuybackRowProps,
  },
  MinQuantity: {
    component: withHydrationOnDemand({on: ['visible']})(MinQuantity),
    propsMapper: generateMinQuantityProps,
  },
  FeaturedSnippet: {
    component: withHydrationOnDemand({on: ['visible']})(ComparedProducts),
    propsMapper: generateComparedProductsProps,
  },
  AdxBanner: {
    component: withHydrationOnDemand({on: ['visible']})(AdxBanner),
    propsMapper: generateAdsBannerProps,
  },
}

const ADD_TO_CART_BUTTON_TEXT = {
  DEFAULT: 'Sepete ekle',
  IS_IN_STOCK: 'Gelince bilgi ver',
  IS_PRE_ORDER: 'Ön sipariş ver',
}

const OPTIONS_TYPE = {
  ROW: 'Row',
  FULL: 'Full'
}

const EVENT_BUS_NAMES = {
  VARIANT_CHANGE: 'variantChangedForProductDetail',
  DUE_DATE: 'voltran.dueDate',
  VAS_ADD: 'CAT.VASAdd',
  VAS_REMOVE: 'CAT.VASRemove',
  VAS_RESET: 'CAT.VASReset',
  lOCATION_CHANGE: 'shipping.voltran.shippingLocation.location.change',
  FASTER_SELLER_CLICK: 'storefront.voltran.deliveryOptions.fasterSeller.click',
  FETCH_PDP_CAMPAIGN: 'FetchPDPCampaign',
  LOYALTY_INFO: 'voltran.loyaltyInfo',
}

const PRODUCT_COOKIE_PREFIXES = [
  'dgr_',
  'hb_adult',
  'statsig_'
]

export {
  PRODUCT_DETAIL_VIEW_TYPES,
  PRODUCT_STATUS_TYPES,
  PRODUCT_DETAIL_CAROUSEL_IMAGE_SIZES,
  PRODUCT_DETAIL_EXTENDED_CAROUSEL_THUMBNAIL_SIZES,
  PRODUCT_DETAIL_COMPONENTS,
  ADD_TO_CART_BUTTON_TEXT,
  OPTIONS_TYPE,
  EVENT_BUS_NAMES,
  INTERSECTION_ROOT_MARGIN,
  PRODUCT_INFORMATION_TEXT,
  PRODUCT_PAYMENT_TEXT,
  PRODUCT_COOKIE_PREFIXES
}
